import { Button, ButtonGroup } from "@msuite/ui/button";
import { ContentCard } from "@msuite/ui/content-card";
import { Divider } from "@msuite/ui/divider";
import { icon } from "@msuite/ui/icons";
import { resolver } from "@msuite/ui/libs/form";
import { useTranslation } from "@msuite/ui/localization";
import { invalidate, useQueryClient } from "@msuite/ui/query";
import { toast } from "@msuite/ui/toast";
import { responseHandler } from "@msuite/ui/utils";
import { type BusinessHoursDay, DEFAULT_BUSINESS_HOURS } from "@msuite/utils/incoming-invoices";
import {
	type PatchOrganizationBusinessHoursSchema,
	patchOrganizationBusinessHoursSchema,
} from "@msuite/utils/schemas";
import { FormProvider, useForm } from "react-hook-form";
import { BusinessHoursEditor } from "@/components/business-hours";
import { client } from "@/hono";

/**
 * The organisation's business hours — the ones every workspace without its own follows. Nothing
 * stored means the default calendar, so the editor opens on it and resetting stores nothing again.
 */
export function OrganizationBusinessHours() {
	/** Context */
	const { t } = useTranslation();

	/** Hooks */
	const qc = useQueryClient();
	const methods = useForm<PatchOrganizationBusinessHoursSchema>({
		resolver: resolver(patchOrganizationBusinessHoursSchema),
		defaultValues: async () => {
			const response = await client.organizations.settings.$get();
			const data = await responseHandler(response);
			const stored = data.organizationSettings?.businessHours as Array<BusinessHoursDay> | null;
			return { businessHours: stored ?? DEFAULT_BUSINESS_HOURS };
		},
	});

	/** Functions */
	async function saveBusinessHours(
		businessHours: PatchOrganizationBusinessHoursSchema["businessHours"],
	) {
		const response = await client.organizations.settings["business-hours"].$patch({
			json: { businessHours },
		});
		await responseHandler(response);
		await qc.invalidateQueries({ predicate: invalidate(["organization-settings"]) });
		toast.success(t("general.saved"));
	}

	async function handleOnSubmit(json: PatchOrganizationBusinessHoursSchema) {
		await saveBusinessHours(json.businessHours);
		methods.reset(json);
	}

	async function handleOnReset() {
		await saveBusinessHours(null);
		methods.reset({ businessHours: DEFAULT_BUSINESS_HOURS });
	}

	/** Render */
	return (
		<FormProvider {...methods}>
			<ContentCard
				title={t("organization.business_hours")}
				icon={icon("Clock")}
				as="form"
				contentClassName="v-stack gap-4"
				onSubmit={methods.handleSubmit(handleOnSubmit)}
				menuItems={[
					{
						label: t("organization.reset_business_hours"),
						icon: icon("RotateCcw"),
						onClick: handleOnReset,
					},
				]}
			>
				<p className="label">{t("organization.business_hours_hint")}</p>
				<p className="label">{t("organization.business_hours_organization_hint")}</p>
				{!methods.formState.isLoading && <BusinessHoursEditor />}
				<Divider variant="content-card" />
				<ButtonGroup className="justify-end">
					<Button
						type="submit"
						variant="primary"
						isLoading={methods.formState.isSubmitting}
						isDisabled={!methods.formState.isDirty}
					>
						{t("general.submit")}
					</Button>
				</ButtonGroup>
			</ContentCard>
		</FormProvider>
	);
}
